// A2: Work Management TypeScript types + constants

export interface Goal {
  id: string;
  title: string;
  description: string | null;
  status: GoalStatus;
  priority: Priority;
  color: string | null;
  icon: string | null;
  tags: string[];
  start_date: string | null;
  due_date: string | null;
  completed_at: string | null;
  owner_id: string | null;
  is_published: boolean;
  created_at: string;
  updated_at: string | null;
  project_count?: number;
  owner?: Owner | null;
}

export interface Project {
  id: string;
  title: string;
  description: string | null;
  status: ProjectStatus;
  priority: Priority;
  goal_id: string | null;
  tags: string[];
  start_date: string | null;
  due_date: string | null;
  completed_at: string | null;
  owner_id: string | null;
  is_published: boolean;
  created_at: string;
  updated_at: string | null;
  task_count?: number;
  owner?: Owner | null;
}

export interface Task {
  id: string;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: Priority;
  project_id: string | null;
  goal_id: string | null;
  owner_id: string | null;
  assignee_id: string | null;
  tags: string[];
  scheduled_at: string | null;
  due_date: string | null;
  completed_at: string | null;
  estimated_minutes: number | null;
  actual_minutes: number | null;
  is_published: boolean;
  created_at: string;
  updated_at: string | null;
  entity_refs?: EntityRef[];
  owner?: Owner | null;
}

export interface EntityRef {
  id: string;
  goal_id: string | null;
  project_id: string | null;
  task_id: string | null;
  entity_type: string;
  entity_id: string;
  label: string | null;
  created_at: string | null;
}

export interface Owner {
  id: string;
  display_name: string;
  avatar_url?: string | null;
  role?: string;
}

// ── Status + Priority Constants ──────────────────────────────────

export const GOAL_STATUSES = ['draft', 'active', 'completed', 'cancelled', 'archived'] as const;
export type GoalStatus = typeof GOAL_STATUSES[number];

export const PROJECT_STATUSES = ['draft', 'active', 'on_hold', 'completed', 'cancelled'] as const;
export type ProjectStatus = typeof PROJECT_STATUSES[number];

export const TASK_STATUSES = ['todo', 'in_progress', 'in_review', 'blocked', 'completed', 'cancelled'] as const;
export type TaskStatus = typeof TASK_STATUSES[number];

export const PRIORITIES = ['low', 'medium', 'high', 'critical'] as const;
export type Priority = typeof PRIORITIES[number];

// ── Status cycle for inline toggle ──────────────────────────────
export const TASK_STATUS_CYCLE: TaskStatus[] = ['todo', 'in_progress', 'in_review', 'completed'];

// ── Accent Color Classes (FIX-2: explicit strings, NOT interpolated) ─

export const ACCENT_CLASSES = {
  emerald: { bg: 'bg-emerald-900/40', fill: 'bg-emerald-500', border: 'border-l-emerald-500', text: 'text-emerald-300', ring: 'ring-emerald-500/30' },
  teal:    { bg: 'bg-teal-900/40', fill: 'bg-teal-500', border: 'border-l-teal-500', text: 'text-teal-300', ring: 'ring-teal-500/30' },
  violet:  { bg: 'bg-violet-900/40', fill: 'bg-violet-500', border: 'border-l-violet-500', text: 'text-violet-300', ring: 'ring-violet-500/30' },
  sky:     { bg: 'bg-sky-900/40', fill: 'bg-sky-500', border: 'border-l-sky-500', text: 'text-sky-300', ring: 'ring-sky-500/30' },
  red:     { bg: 'bg-red-900/40', fill: 'bg-red-500', border: 'border-l-red-500', text: 'text-red-300', ring: 'ring-red-500/30' },
  amber:   { bg: 'bg-amber-900/40', fill: 'bg-amber-500', border: 'border-l-amber-500', text: 'text-amber-300', ring: 'ring-amber-500/30' },
  gray:    { bg: 'bg-gray-700', fill: 'bg-gray-500', border: 'border-l-gray-500', text: 'text-gray-400', ring: 'ring-gray-500/30' },
} as const;
export type AccentColor = keyof typeof ACCENT_CLASSES;

// ── Color Maps ──────────────────────────────────────────────────

export const ENTITY_COLORS = {
  goal: { accent: 'emerald' as AccentColor, gradient: 'from-emerald-600 to-teal-500' },
  project: { accent: 'teal' as AccentColor, gradient: 'from-teal-600 to-cyan-500' },
  task: { accent: 'violet' as AccentColor, gradient: 'from-violet-600 to-purple-500' },
} as const;

export const STATUS_CONFIG: Record<string, { icon: string; dark: string; light: string }> = {
  draft:       { icon: '○', dark: 'bg-gray-700 text-gray-400', light: 'bg-gray-100 text-gray-600' },
  todo:        { icon: '○', dark: 'bg-gray-700 text-gray-400', light: 'bg-gray-100 text-gray-600' },
  active:      { icon: '◑', dark: 'bg-emerald-900/40 text-emerald-300', light: 'bg-emerald-50 text-emerald-700' },
  in_progress: { icon: '◑', dark: 'bg-violet-900/40 text-violet-300', light: 'bg-violet-50 text-violet-700' },
  in_review:   { icon: '◎', dark: 'bg-sky-900/40 text-sky-300', light: 'bg-sky-50 text-sky-700' },
  on_hold:     { icon: '⏸', dark: 'bg-amber-900/40 text-amber-300', light: 'bg-amber-50 text-amber-700' },
  blocked:     { icon: '⊗', dark: 'bg-red-900/40 text-red-300', light: 'bg-red-50 text-red-700' },
  completed:   { icon: '✓', dark: 'bg-emerald-900/40 text-emerald-300', light: 'bg-emerald-50 text-emerald-700' },
  cancelled:   { icon: '✕', dark: 'bg-gray-700 text-gray-500', light: 'bg-gray-100 text-gray-400' },
  archived:    { icon: '▣', dark: 'bg-gray-700 text-gray-500', light: 'bg-gray-100 text-gray-400' },
};

export const PRIORITY_CONFIG: Record<Priority, { icon: string; dark: string; light: string }> = {
  critical: { icon: '↑↑', dark: 'bg-red-900/40 text-red-300', light: 'bg-red-50 text-red-700' },
  high:     { icon: '↑',  dark: 'bg-orange-900/40 text-orange-300', light: 'bg-orange-50 text-orange-700' },
  medium:   { icon: '—',  dark: 'bg-gray-700 text-gray-500', light: 'bg-gray-200 text-gray-600' },
  low:      { icon: '↓',  dark: 'bg-gray-800 text-gray-600', light: 'bg-gray-100 text-gray-500' },
};

// ── Task row border color by status ──────────────────────────────
export const TASK_ROW_BORDER: Record<string, string> = {
  in_progress: 'border-l-2 border-l-violet-500/40',
  in_review:   'border-l-2 border-l-sky-500/40',
  completed:   'border-l-2 border-l-emerald-500/30',
  blocked:     'border-l-2 border-l-red-500/60',
  todo:        'border-l-2 border-l-gray-600/30',
};

// ── Kanban column config ─────────────────────────────────────────
export const KANBAN_COLUMNS: { id: TaskStatus; label: string; accent: AccentColor }[] = [
  { id: 'todo', label: 'To Do', accent: 'gray' },
  { id: 'in_progress', label: 'In Progress', accent: 'violet' },
  { id: 'in_review', label: 'In Review', accent: 'sky' },
  { id: 'blocked', label: 'Blocked', accent: 'red' },
  { id: 'completed', label: 'Done', accent: 'emerald' },
];
